/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/recorder.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-16 (Step 6)  | Claude Sonnet 4.6 | Recorder v0 (Step 6)   | Created from scratch |
 * | 2 | 2026-06-16 (bugfix)  | Claude Sonnet 4.6 | silent dumpTree failure | try-catch; coord-only fallback |
 * | 3 | 2026-06-16 (fix1+2)  | Claude Sonnet 4.6 | coord-only steps on real devices | Fix 1: hitTestTree interactive preference; Fix 2: relative candidates |
 * | 4 | 2026-06-16 (edit)    | Claude Sonnet 4.6 | Step editing / reliability | Remove className fallback (was silently wrong); fix relative description; fallbackCoord on TapStep; deleteStep / setReplaceMode / reorderCandidate / removeCandidate; auto-stop after replace; onAutoStop callback |
 * | 5 | 2026-06-16 (order)   | Claude Sonnet 4.6 | Step reorder + edit cmds   | moveStep(index, direction) — swap adjacent; updateStep(index, step) — replace in-place for editing non-tap steps |
 * | 6 | 2026-06-17 (locator) | Claude Sonnet 4.6 | Locator accuracy           | Ancestor walk (buildParentMap/getAncestors): hit-node child lacking identifiers now climbs to parent with contentDesc; contentDesc prioritised over text; updateCandidate/addCandidate for manual editing |
 * | 7 | 2026-06-17 (unique)  | Claude Sonnet 4.6 | Uniqueness-first locator   | countMatches/getMatchIndex helpers; buildSelector now checks uniqueness against flat tree — unique identifiers win over non-unique regardless of attribute type; non-unique candidates recorded with positional index; ancestor priority flipped to contentDesc→text→a11yId (container resourceIds are almost always shared) |
 * | 8 | 2026-06-17 (descend) | Claude Sonnet 4.6 | Compose container fix      | buildSelector Phase 3: descendant search — when hitTestPreferInteractive returns a clickable container whose own/ancestor identifiers are all non-unique (Jetpack Compose NavigationBarItem pattern), search children/grandchildren at the tap point for unique contentDesc/text; tapPoint param added; description also checks tap-point child; #handleClick passes {x,y} as tapPoint |
 * | 9 | 2026-06-17 (labels)  | Claude Sonnet 4.6 | Label-first architecture   | Split uniqueCandidates into uniqueLabels + uniqueIds; Phase 3 now gated on uniqueLabels.length===0 (not all unique candidates) so it runs even when container has a unique a11yId; Phase 2 gated on (labels+ids)===0; Phase 4 merge order: labels→ids→ambiguous; fixes Cannondale tab_bar_compose_view unique-but-wrong a11yId case |
 * | 10 | 2026-06-17 (fingerprint) | Claude Sonnet 4.6 | Tier-1 healing     | buildSelector now computes Fingerprint at record time via computeFingerprint(@mimiq/healing); screenSize? param added; #handleClick passes canvas dimensions |
 * | 11 | 2026-06-18 (dnd)       | Claude Sonnet 4.6 | Phase 3 dnd-kit    | reorderStepsByIds(ids[]) — reorders step list to match a new ID order; used by dnd-kit after drag |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx             (RecorderV0, StepsListener, IRStep)
 * - apps/studio/src/device/recorder.test.ts (unit tests)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * Pure functions (testable in Node.js, no browser APIs):
 * - mapToDeviceCoords(cssX, cssY, displayW, displayH, deviceW, deviceH): [x, y]
 * - hitTestTree(nodes, x, y): A11yNode | null
 * - buildSelector(node, flatNodes?, ancestors?, tapPoint?): Selector
 *   WHY no className: android.view.View matches hundreds of nodes. DFS returns
 *   the first one — almost never the intended element. It produces false-positive
 *   ok results that mask replay failures. Better to be honest: no candidates →
 *   coord-only fallback (fallbackCoord on TapStep).
 *   tapPoint feeds Phase 3 descendant search — needed for Compose NavigationBarItem
 *   pattern where the clickable container has no unique identifier but its children do.
 *
 * Class:
 * - RecorderV0  — canvas pointer listener → dump tree → hit-test → IR step → ADB tap
 *   Methods:
 *     start(adb, canvas, opts?)  — attach listener; opts.onAutoStop fires after a replace
 *     stop()                     — detach listener (manual; does NOT fire onAutoStop)
 *     clearSteps()               — wipe step list
 *     addStep(step)              — append arbitrary step (command toolbar)
 *     deleteStep(index)          — remove one step by index
 *     moveStep(index, direction) — swap step[index] with step[index±1]; no-op at bounds
 *     updateStep(index, step)    — replace step[index] in-place (edit non-tap steps)
 *     setReplaceMode(index)      — next tap replaces step[index] instead of appending
 *     reorderCandidate(si, ci)   — move candidate[ci] in step[si] to position 0
 *     removeCandidate(si, ci)    — remove candidate[ci] from step[si]
 *     get steps()
 *     subscribe(listener)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. className is intentionally NOT added as a candidate. It produces false
 *    positives (first DFS match for "android.view.View" is not the tapped node).
 *    If no real identifier exists → fallbackCoord on the TapStep is set instead.
 * 2. Two-phase hit-test: prefer interactive (clickable|focusable|longClickable),
 *    fall back to deepest node of any kind (skips statusBarScrim overlays).
 * 3. buildSelector description for relative candidates extracts the anchor label
 *    ("below 'Register Bike'") rather than falling back to className.
 * 4. After setReplaceMode(i): the next pointerdown replaces steps[i] and the
 *    recorder auto-stops (via #autoStop, which fires opts.onAutoStop). This lets
 *    the caller sync React recording state without polling.
 * 5. reorderCandidate / removeCandidate only work on TapStep (only type with
 *    candidates). Non-tap steps are silently ignored.
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - dumpTree can fail during scrcpy streaming (OEM ADB quirks). #handleClick
 *   wraps it in try-catch and falls back to coord-only so recording never breaks.
 * - canvas.getBoundingClientRect() returns 0 in jsdom. Tests use fake canvas.
 *
 * =============================================================================
 */

import type { Adb } from "@yume-chan/adb";
import type { A11yNode } from "./treeParser.js";
import type { IRStep, TapStep, LaunchAppStep, Selector, Candidate } from "@mimiq/ir";
import { generateId } from "@mimiq/ir";
import { computeFingerprint } from "@mimiq/healing";
import { generateXPath } from "./xpathUtils.js";

/* ── App launch detection helpers ───────────────────────────────────────────
 *
 * When the user taps an app icon on the home screen the foreground package
 * changes from a launcher to the target app. We detect this and record a
 * LaunchAppStep instead of a coordinate-based TapStep.
 *
 * Only triggered when the pre-tap foreground is a known launcher package,
 * so in-app taps have ZERO extra latency.
 */

const LAUNCHER_PACKAGES = new Set([
  // Google / Pixel
  "com.google.android.apps.nexuslauncher",
  "com.google.android.launcher",
  // Samsung
  "com.sec.android.app.launcher",
  "com.samsung.android.app.appsedge",
  "com.samsung.android.app.cocktailbarservice",
  // Xiaomi / MIUI
  "com.miui.home",
  // AOSP generic
  "com.android.launcher",
  "com.android.launcher2",
  "com.android.launcher3",
  // OnePlus OxygenOS (various generations)
  "com.oneplus.launcher",          // OxygenOS 11 and earlier
  "net.oneplus.deskhome",          // OnePlus 8/9/10 series OxygenOS
  "com.oplus.launcher",            // OnePlus ColorOS merge (2022+)
  // Oppo / ColorOS
  "com.oppo.launcher",
  "com.coloros.launcher",
  // Vivo / FuntouchOS
  "com.vivo.launcher",
  "com.bbk.launcher2",
  // Huawei / HarmonyOS / EMUI
  "com.huawei.android.launcher",
  "com.hihonor.android.launcher",  // Honor (split from Huawei)
  // Realme
  "com.realme.launcher",
  // Motorola
  "com.motorola.launcher3",
  "com.motorola.launcher",
  // LG (legacy)
  "com.lge.launcher3",
  "com.lge.launcher2",
  // Popular 3rd-party launchers
  "app.lawnchair",
  "com.teslacoilsw.launcher",       // Nova Launcher
  "com.actionlauncher.playstore",   // Action Launcher
  "com.microsoft.launcher",         // Microsoft Launcher
  "com.smart.launcher5",
]);

function isLauncherPackage(pkg: string): boolean {
  if (LAUNCHER_PACKAGES.has(pkg)) return true;
  // Generic heuristic: package names ending in ".launcher", ".home", or ".deskhome"
  const lower = pkg.toLowerCase();
  return (
    lower.endsWith(".launcher") ||
    lower.endsWith("launcher") ||
    lower.endsWith(".home") ||
    lower.endsWith(".deskhome") ||
    lower.includes("launcher") && (lower.startsWith("com.") || lower.startsWith("net."))
  );
}

/**
 * Get the current foreground app package.
 * Tries multiple dumpsys commands for cross-device compatibility.
 * Returns null if all approaches fail (detection skipped gracefully).
 */
async function getForegroundPackage(adb: Adb): Promise<string | null> {
  // Extract package name from common dumpsys output patterns
  function extractPkg(raw: string): string | null {
    if (!raw || raw.trim() === "" || raw.includes("null")) return null;

    // Pattern 1: mCurrentFocus=Window{abc u0 com.example.app/Activity}
    let m = raw.match(/mCurrentFocus=\S+\s+([\w.]+)\//);
    if (m?.[1] && m[1].includes(".")) return m[1];

    // Pattern 2: mResumedActivity: ActivityRecord{... com.example.app/.Activity ...}
    m = raw.match(/mResumedActivity:.*?\s+([\w.]+)\//);
    if (m?.[1] && m[1].includes(".")) return m[1];

    // Pattern 3: mFocusedApp=AppWindowToken{... com.example.app}
    m = raw.match(/mFocusedApp=\S+\s+\S+\s+([\w.]+)[/\s}]/);
    if (m?.[1] && m[1].includes(".")) return m[1];

    // Pattern 4: ActivityRecord{... com.example.app/.Activity t3}
    m = raw.match(/ActivityRecord\{[^}]+\s+([\w.]+)\//);
    if (m?.[1] && m[1].includes(".")) return m[1];

    return null;
  }

  // Try 1: dumpsys window (fastest, works on most devices)
  try {
    const raw = await adb.subprocess.noneProtocol.spawnWaitText([
      "sh", "-c", "dumpsys window 2>/dev/null | grep -E 'mCurrentFocus|mFocusedApp' | head -5",
    ]);
    const pkg = extractPkg(raw);
    if (pkg) return pkg;
  } catch { /* try next */ }

  // Try 2: dumpsys activity activities (more reliable on newer Android / ColorOS)
  try {
    const raw = await adb.subprocess.noneProtocol.spawnWaitText([
      "sh", "-c", "dumpsys activity activities 2>/dev/null | grep -E 'mResumedActivity|mFocusedActivity' | head -5",
    ]);
    const pkg = extractPkg(raw);
    if (pkg) return pkg;
  } catch { /* try next */ }

  // Try 3: am stack list (older fallback, works on Android 7-9)
  try {
    const raw = await adb.subprocess.noneProtocol.spawnWaitText([
      "sh", "-c", "am stack list 2>/dev/null | grep -i resumed | head -3",
    ]);
    const pkg = extractPkg(raw);
    if (pkg) return pkg;
  } catch { /* all approaches failed */ }

  return null;
}

function sleepMs(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Poll until the foreground package changes to a non-launcher app.
 * Returns the new package name, or null if no change within maxMs.
 */
async function waitForLaunch(adb: Adb, prePkg: string, maxMs = 2000): Promise<string | null> {
  const poll = 200;
  let elapsed = 0;
  while (elapsed < maxMs) {
    await sleepMs(poll);
    elapsed += poll;
    const cur = await getForegroundPackage(adb);
    if (cur && cur !== prePkg && !isLauncherPackage(cur)) return cur;
  }
  return null;
}

/* ── Pure: coordinate mapping ────────────────────────────────────────────────*/

export function mapToDeviceCoords(
  cssX: number,
  cssY: number,
  displayW: number,
  displayH: number,
  deviceW: number,
  deviceH: number,
): [number, number] {
  return [
    Math.round((cssX / displayW) * deviceW),
    Math.round((cssY / displayH) * deviceH),
  ];
}

/* ── Pure: hit-test accessibility tree ───────────────────────────────────────
 *
 * Two-phase search in reverse child order (last sibling is visually on top):
 *   Phase 1 — prefer deepest interactive node (clickable | focusable | longClickable).
 *   Phase 2 — fall back to deepest node of any kind.
 */
export function isInteractive(node: A11yNode): boolean {
  return node.clickable || node.focusable || node.longClickable;
}

function hitTestDeepest(nodes: A11yNode[], x: number, y: number): A11yNode | null {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]!;
    const { left, top, right, bottom } = node.bounds;
    if (x >= left && x < right && y >= top && y < bottom) {
      const childHit = hitTestDeepest(node.children, x, y);
      return childHit ?? node;
    }
  }
  return null;
}

function hitTestPreferInteractive(nodes: A11yNode[], x: number, y: number): A11yNode | null {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]!;
    const { left, top, right, bottom } = node.bounds;
    if (x >= left && x < right && y >= top && y < bottom) {
      const childHit = hitTestPreferInteractive(node.children, x, y);
      if (childHit) return childHit;
      if (isInteractive(node)) return node;
    }
  }
  return null;
}

export function hitTestTree(nodes: A11yNode[], x: number, y: number): A11yNode | null {
  return hitTestPreferInteractive(nodes, x, y) ?? hitTestDeepest(nodes, x, y);
}

/* ── Pure: flatten tree into a list (pre-order) ──────────────────────────── */

export function flattenTree(nodes: A11yNode[]): A11yNode[] {
  const result: A11yNode[] = [];
  function walk(ns: A11yNode[]) {
    for (const n of ns) { result.push(n); walk(n.children); }
  }
  walk(nodes);
  return result;
}

/* ── Pure: build a child → parent map for ancestor walks ─────────────────── */

export function buildParentMap(roots: A11yNode[]): Map<A11yNode, A11yNode> {
  const map = new Map<A11yNode, A11yNode>();
  function walk(nodes: A11yNode[], parent: A11yNode | null) {
    for (const node of nodes) {
      if (parent) map.set(node, parent);
      walk(node.children, node);
    }
  }
  walk(roots, null);
  return map;
}

/** Returns ancestor chain from nearest parent up, capped at maxDepth. */
export function getAncestors(
  node: A11yNode,
  parentMap: Map<A11yNode, A11yNode>,
  maxDepth = 4,
): A11yNode[] {
  const ancestors: A11yNode[] = [];
  let current = parentMap.get(node);
  while (current && ancestors.length < maxDepth) {
    ancestors.push(current);
    current = parentMap.get(current);
  }
  return ancestors;
}

/* ── Pure: uniqueness helpers ─────────────────────────────────────────────── */

/** Count nodes in the flat tree whose attribute matches strategy+value. */
export function countMatches(
  flat: A11yNode[],
  strategy: "a11yId" | "contentDesc" | "text",
  value: string,
): number {
  return flat.filter((n) => {
    if (strategy === "a11yId") return n.resourceId === value;
    if (strategy === "contentDesc") return n.contentDesc === value;
    return n.text === value;
  }).length;
}

/**
 * 0-based position of targetNode among nodes matching strategy+value,
 * sorted by visual position (bounds.top → bounds.left).
 *
 * Must use the SAME sort order as resolver.ts findByCandidate() so that
 * the index recorded here is the correct index to pass at replay time.
 *
 * Why bounds-sorted instead of DFS order: RecyclerView / Compose LazyColumn
 * enumerate view holders in recycling order which is unrelated to their
 * vertical screen position. Sorting by top makes index=0 mean "topmost row".
 *
 * Falls back to bounds-distance comparison if targetNode is not found by
 * reference (can happen when hitTest returns a deep child whose PARENT holds
 * the matching attribute — in that case we find the closest ancestor in the
 * sorted matches list by bounds overlap).
 */
export function getMatchIndex(
  flat: A11yNode[],
  strategy: "a11yId" | "contentDesc" | "text",
  value: string,
  targetNode: A11yNode,
): number {
  const matches = flat.filter((n) => {
    if (strategy === "a11yId") return n.resourceId === value;
    if (strategy === "contentDesc") return n.contentDesc === value;
    return n.text === value;
  });

  // Sort by visual top-to-bottom, left-to-right (same order as resolver)
  matches.sort((a, b) => {
    const topDiff = a.bounds.top - b.bounds.top;
    return topDiff !== 0 ? topDiff : a.bounds.left - b.bounds.left;
  });

  // Try exact reference match first
  const exactIdx = matches.findIndex((n) => n === targetNode);
  if (exactIdx >= 0) return exactIdx;

  // Fallback: find by bounds overlap — handles case where hitTest returned
  // a child while the matching attribute is on an ancestor (or vice versa).
  // Use the match whose bounds most closely contain or overlap targetNode's center.
  const cx = (targetNode.bounds.left + targetNode.bounds.right) / 2;
  const cy = (targetNode.bounds.top + targetNode.bounds.bottom) / 2;
  const containingIdx = matches.findIndex(
    (n) => cx >= n.bounds.left && cx < n.bounds.right && cy >= n.bounds.top && cy < n.bounds.bottom,
  );
  if (containingIdx >= 0) return containingIdx;

  // Last resort: find closest by center distance
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < matches.length; i++) {
    const n = matches[i]!;
    const ncx = (n.bounds.left + n.bounds.right) / 2;
    const ncy = (n.bounds.top + n.bounds.bottom) / 2;
    const dist = Math.abs(ncx - cx) + Math.abs(ncy - cy);
    if (dist < bestDist) { bestDist = dist; bestIdx = i; }
  }
  return bestIdx;
}

/* ── Pure: find a Maestro-style relative candidate ───────────────────────── */

function buildRelativeCandidate(node: A11yNode, flatNodes: A11yNode[]): Candidate | null {
  const cx = (node.bounds.left + node.bounds.right) / 2;
  let best: A11yNode | null = null;
  let bestDist = Infinity;

  for (const candidate of flatNodes) {
    if (candidate === node) continue;
    const anchorLabel = candidate.text || candidate.resourceId;
    if (!anchorLabel) continue;
    if (candidate.bounds.bottom > node.bounds.top) continue;
    const anchorCx = (candidate.bounds.left + candidate.bounds.right) / 2;
    const targetHalfW = (node.bounds.right - node.bounds.left) / 2;
    if (Math.abs(anchorCx - cx) > targetHalfW + 50) continue;
    const dist = node.bounds.top - candidate.bounds.bottom;
    if (dist < bestDist) { bestDist = dist; best = candidate; }
  }

  if (!best) return null;
  const anchorLabel = best.text || best.resourceId;
  const strategy = best.text ? "text" : "a11yId";
  return { strategy: "relative", value: `below(${strategy}='${anchorLabel}')`, addedBy: "recorder" };
}

/* ── Pure: build Selector from an A11yNode ───────────────────────────────────
 *
 * Label-first, uniqueness-aware algorithm.
 * Candidates are split into three buckets then merged in priority order:
 *   uniqueLabels   — unique contentDesc/text  → most resilient; survives refactors
 *   uniqueIds      — unique a11yId            → reliable but tied to layout XML
 *   ambiguous      — count > 1 with index     → positional fallback
 *
 * Phase 1 — Direct hit node  (contentDesc → text → a11yId)
 *   Each attribute: count=1 → unique bucket | count>1 → ambiguous with index
 *   No flat tree provided → treat all as unique (safe default, no tree context).
 *
 * Phase 2 — Ancestor walk  (contentDesc → text → a11yId)
 *   Runs ONLY when the hit node has NO identifier at all (no unique label,
 *   no unique ID).  A bare icon leaf with no attrs climbs to the nearest parent
 *   that has a label or ID.  Stops after the first ancestor that contributes a
 *   unique label.
 *
 * Phase 3 — Descendant search  (contentDesc/text only, no a11yId)
 *   Runs when no unique LABEL exists yet — even if a unique a11yId was found.
 *   Required for Jetpack Compose NavigationBar: the clickable CONTAINER is hit
 *   by hitTestPreferInteractive (container is clickable=true), but the per-tab
 *   labels ("Garage", "Ride", …) live on NON-interactive child nodes.
 *   With tapPoint: children at the tap coordinate are visited FIRST, so the
 *   correct tab label is found regardless of child order.
 *
 * Phase 4 — Merge: [uniqueLabels, uniqueIds, ambiguous]
 *   Labels always beat IDs as the primary candidate.
 *
 * Phase 5 — Relative locator: only when all else fails.
 *
 * className is deliberately NOT added (produces false-positive first-DFS match).
 */
export function buildSelector(
  node: A11yNode,
  flatNodes?: A11yNode[],
  ancestors?: A11yNode[],
  tapPoint?: { x: number; y: number },
  screenSize?: { width: number; height: number },
): Selector {
  const flat = flatNodes ?? [];
  const hasFlat = flat.length > 0;

  const uniqueLabels: Candidate[] = [];    // unique contentDesc/text — preferred
  const uniqueIds: Candidate[] = [];       // unique a11yId — fallback
  const ambiguousCandidates: Candidate[] = []; // count > 1 with positional index

  /** Route one attribute into the correct bucket based on type and uniqueness. */
  function categorize(
    strategy: "a11yId" | "contentDesc" | "text",
    value: string | undefined,
    sourceNode: A11yNode,
  ): void {
    if (!value) return;
    const isDup = [...uniqueLabels, ...uniqueIds, ...ambiguousCandidates].some(
      (c) => c.strategy === strategy && c.value === value,
    );
    if (isDup) return;

    if (!hasFlat) {
      if (strategy === "a11yId") uniqueIds.push({ strategy, value, addedBy: "recorder" });
      else uniqueLabels.push({ strategy, value, addedBy: "recorder" });
      return;
    }
    const count = countMatches(flat, strategy, value);
    if (count === 1) {
      if (strategy === "a11yId") uniqueIds.push({ strategy, value, addedBy: "recorder" });
      else uniqueLabels.push({ strategy, value, addedBy: "recorder" });
    } else if (count > 1) {
      const index = getMatchIndex(flat, strategy, value, sourceNode);
      ambiguousCandidates.push({ strategy, value, index, addedBy: "recorder" });
    }
    // count === 0: stale value not in tree → skip
  }

  // ── Phase 1: direct node ─────────────────────────────────────────────────
  categorize("contentDesc", node.contentDesc, node);
  categorize("text", node.text, node);
  categorize("a11yId", node.resourceId, node);

  // ── Phase 2: ancestor walk ───────────────────────────────────────────────
  // Only when the hit node has NO identifier of any kind (bare icon leaf with
  // no attributes).  Stops at the first ancestor that contributes a unique label.
  //
  // CONTAINER SKIP: ancestors whose bounds area is > 4× the hit node's area are
  // list/page containers (RecyclerView, ScrollView, full-page ViewGroup) whose
  // unique label identifies the whole container, not the specific row.
  // Skipping them lets Phase 3 find the row's own unique child text instead.
  // Example: RecyclerView contentDesc="bike_parts__lst_parts" (11× larger than a
  //   row) was selected as the locator, then tapped at its center → always row 6.
  if (uniqueLabels.length === 0 && uniqueIds.length === 0 && ancestors && ancestors.length > 0) {
    const hitArea = (node.bounds.right - node.bounds.left) * (node.bounds.bottom - node.bounds.top);
    for (const anc of ancestors) {
      if (hasFlat && hitArea > 0) {
        const ancArea = (anc.bounds.right - anc.bounds.left) * (anc.bounds.bottom - anc.bounds.top);
        if (ancArea > hitArea * 4) continue; // skip large container ancestors
      }
      const beforeLabels = uniqueLabels.length;
      categorize("contentDesc", anc.contentDesc, anc);
      categorize("text", anc.text, anc);
      categorize("a11yId", anc.resourceId, anc);
      if (uniqueLabels.length > beforeLabels) break;
    }
  }

  // ── Phase 3: descendant search ───────────────────────────────────────────
  // Runs when no unique LABEL found yet (uniqueLabels empty).
  // A unique a11yId on the container does NOT block this phase — container IDs
  // identify the widget but don't distinguish which item was tapped.
  // tapPoint-priority: children at the tap coordinates are visited first so the
  // correct tab is identified even when all tabs share the container's resource-id.
  if (uniqueLabels.length === 0 && node.children.length > 0) {
    const visitOrder: A11yNode[] = [];

    if (tapPoint) {
      const { x, y } = tapPoint;
      for (const child of node.children) {
        const { left, top, right, bottom } = child.bounds;
        if (x >= left && x < right && y >= top && y < bottom) {
          visitOrder.push(child);
          for (const gc of child.children) visitOrder.push(gc);
        }
      }
    }

    const visited = new Set(visitOrder);
    for (const child of node.children) {
      if (!visited.has(child)) {
        visitOrder.push(child);
        for (const gc of child.children) {
          if (!visited.has(gc)) visitOrder.push(gc);
        }
      }
    }

    for (const desc of visitOrder) {
      const before = uniqueLabels.length;
      categorize("contentDesc", desc.contentDesc, desc);
      categorize("text", desc.text, desc);
      // a11yId intentionally skipped — descendant IDs are unreliable
      if (uniqueLabels.length > before) break;
    }
  }

  // ── Phase 4: labels first, then IDs, then ambiguous ──────────────────────
  const candidates: Candidate[] = [...uniqueLabels, ...uniqueIds, ...ambiguousCandidates];

  // ── Phase 5: relative as last resort ─────────────────────────────────────
  if (candidates.length === 0 && hasFlat) {
    const rel = buildRelativeCandidate(node, flat);
    if (rel) candidates.push(rel);
  }

  // ── Phase 6: auto-add indexed XPath as final fallback candidate ──────────
  // Only add when: (a) flat tree available, (b) xpath candidate not already present,
  // (c) generated quality is "indexed" — stable XPath duplicates existing candidates;
  // fragile XPath is worse than coordinate fallback.
  if (hasFlat && ancestors) {
    const xpathResult = generateXPath(node, ancestors, flat);
    if (xpathResult.quality === "indexed" &&
        !candidates.some((c) => c.strategy === "xpath")) {
      candidates.push({ strategy: "xpath", value: xpathResult.xpath, addedBy: "recorder" });
    }
  }

  // ── Description: human-readable label for the step list ─────────────────
  function pickLabel(n: A11yNode): string | null {
    if (n.contentDesc) return n.contentDesc;
    if (n.text) return n.text;
    return null;
  }

  let description = pickLabel(node);
  if (!description && ancestors) {
    for (const anc of ancestors) {
      const lbl = pickLabel(anc);
      if (lbl) { description = lbl; break; }
    }
  }
  // Check child at the tap point — covers Compose container with unlabelled children
  if (!description && tapPoint && node.children.length > 0) {
    const { x, y } = tapPoint;
    outer: for (const child of node.children) {
      const { left, top, right, bottom } = child.bounds;
      if (x >= left && x < right && y >= top && y < bottom) {
        const lbl = pickLabel(child);
        if (lbl) { description = lbl; break; }
        for (const gc of child.children) {
          const glbl = pickLabel(gc);
          if (glbl) { description = glbl; break outer; }
        }
      }
    }
  }
  if (!description) {
    const rel = candidates.find((c) => c.strategy === "relative");
    if (rel) {
      const m = rel.value.match(/^below\(\w+='([^']*)'\)$/);
      description = m ? `below "${m[1]}"` : rel.value;
    } else {
      description = node.resourceId || node.className;
    }
  }

  // Fingerprint for Tier-1 ONNX healing — computed at record time, no ONNX needed
  const fingerprint =
    screenSize && flat.length > 0
      ? computeFingerprint({
          // treeParser.A11yNode is structurally compatible with dal.A11yNode
          node: node as Parameters<typeof computeFingerprint>[0]["node"],
          allNodes: flat as Parameters<typeof computeFingerprint>[0]["allNodes"],
          screenWidth: screenSize.width,
          screenHeight: screenSize.height,
        })
      : undefined;

  return {
    description: description ?? node.className,
    candidates,
    ...(fingerprint ? { fingerprint } : {}),
  };
}

/* ── Injectable deps ──────────────────────────────────────────────────────── */

export interface RecorderDeps {
  dumpTree?: (adb: Adb) => Promise<A11yNode[]>;
  injectTap?: (adb: Adb, x: number, y: number) => Promise<void>;
}

async function defaultInjectTap(adb: Adb, x: number, y: number): Promise<void> {
  await adb.subprocess.noneProtocol.spawnWaitText([
    "input", "tap", x.toString(), y.toString(),
  ]);
}

export type StepsListener = (steps: readonly IRStep[]) => void;

/* ── RecorderV0 ───────────────────────────────────────────────────────────── */

const SWIPE_THRESHOLD_PX = 15; // CSS pixels — movement beyond this = swipe, not tap

export class RecorderV0 {
  #steps: IRStep[] = [];
  #listeners = new Set<StepsListener>();
  #adb: Adb | null = null;
  #canvas: HTMLCanvasElement | null = null;
  #replaceIndex: number | null = null;
  #autoStopCallback: (() => void) | null = null;

  // ── Element capture mode ─────────────────────────────────────────────────
  /**
   * When set, the next tap on the mirror calls this callback with the built
   * Selector instead of creating a TapStep. Used by the QuickAddStep UI to let
   * users click an element on the mirror to fill an element locator field.
   * No step is appended and no ADB tap is injected in this mode.
   */
  #captureCallback: ((sel: Selector) => void) | null = null;

  // ── Gesture tracking state ───────────────────────────────────────────────
  /** Pointer position (CSS px) and timestamp at pointerdown. */
  #gestureStart: { cssX: number; cssY: number; t: number } | null = null;
  /** Live pointer position (CSS px) during drag. */
  #gestureCurrent: { cssX: number; cssY: number } | null = null;
  /** SVG overlay showing the drag trail — appended to canvas.parentElement. */
  #trailSvg: SVGSVGElement | null = null;
  /**
   * Pointer ID from the pointerdown event.
   * pointermove/pointerup are on the document, so we filter by pointer ID
   * to ignore events from other touches/mice while our gesture is active.
   */
  #activePointerId: number | null = null;
  /** Target for move/up/cancel listeners — document in browser, canvas in tests. */
  #docTarget: EventTarget | null = null;

  readonly #dumpTree: (adb: Adb) => Promise<A11yNode[]>;
  readonly #injectTap: (adb: Adb, x: number, y: number) => Promise<void>;

  readonly #onPointerDown: (e: Event) => void = (e: Event) => {
    this.#handlePointerDown(e as PointerEvent);
  };
  // pointermove and pointerup are registered on document (not canvas) so they
  // fire even when the mouse/touch leaves the canvas boundary during a drag.
  readonly #onPointerMove: (e: Event) => void = (e: Event) => {
    const pe = e as PointerEvent;
    if (pe.pointerId !== this.#activePointerId) return; // not our gesture
    this.#handlePointerMove(pe);
  };
  readonly #onPointerUp: (e: Event) => void = (e: Event) => {
    const pe = e as PointerEvent;
    if (pe.pointerId !== this.#activePointerId) return;
    void this.#handlePointerUp(pe);
  };
  readonly #onPointerCancel: (e: Event) => void = (e: Event) => {
    const pe = e as PointerEvent;
    if (pe.pointerId !== this.#activePointerId) return;
    // Gesture cancelled by OS (e.g. incoming call, screenshot) — reset silently
    this.#activePointerId = null;
    this.#gestureStart = null;
    this.#gestureCurrent = null;
    this.#removeSvgTrail();
  };

  constructor(deps: RecorderDeps = {}) {
    this.#dumpTree =
      deps.dumpTree ??
      ((adb) => import("./treeParser.js").then((m) => m.dumpTree(adb)));
    this.#injectTap = deps.injectTap ?? defaultInjectTap;
  }

  // ── Pointer event handlers ───────────────────────────────────────────────

  /** pointerdown — capture gesture start position and show start dot. */
  #handlePointerDown(e: PointerEvent): void {
    const canvas = this.#canvas;
    if (!canvas) return;
    // Prevent browser default drag/select behaviour that would interrupt pointer events
    if (typeof e.preventDefault === "function") e.preventDefault();

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    this.#activePointerId = e.pointerId;
    this.#gestureStart = { cssX, cssY, t: Date.now() };
    this.#gestureCurrent = { cssX, cssY };

    // Create SVG trail overlay (browser only — not available in test/Node environment)
    this.#removeSvgTrail();
    if (typeof document !== "undefined" && canvas.parentElement) {
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("class", "swipe-trail-svg");
      svg.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;";
      const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      dot.setAttribute("cx", String(cssX));
      dot.setAttribute("cy", String(cssY));
      dot.setAttribute("r", "6");
      dot.setAttribute("fill", "rgba(166,227,161,0.85)");
      dot.setAttribute("stroke", "#a6e3a1");
      dot.setAttribute("stroke-width", "2");
      svg.appendChild(dot);
      canvas.parentElement.appendChild(svg);
      this.#trailSvg = svg;
    }
  }

  /** pointermove — update live drag line while finger is held. */
  #handlePointerMove(e: PointerEvent): void {
    if (!this.#gestureStart || !this.#canvas) return;
    const rect = this.#canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    this.#gestureCurrent = { cssX, cssY };

    const svg = this.#trailSvg;
    if (!svg) return;

    // Update or create the drag line
    let line = svg.querySelector("line");
    if (!line) {
      line = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line.setAttribute("stroke", "rgba(166,227,161,0.7)");
      line.setAttribute("stroke-width", "2.5");
      line.setAttribute("stroke-dasharray", "6 3");
      svg.insertBefore(line, svg.firstChild); // behind the dot
    }
    line.setAttribute("x1", String(this.#gestureStart.cssX));
    line.setAttribute("y1", String(this.#gestureStart.cssY));
    line.setAttribute("x2", String(cssX));
    line.setAttribute("y2", String(cssY));

    // Update or create end circle
    let endDot = svg.querySelector("circle.end-dot") as SVGCircleElement | null;
    if (!endDot) {
      endDot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      endDot.setAttribute("class", "end-dot");
      endDot.setAttribute("r", "5");
      endDot.setAttribute("fill", "none");
      endDot.setAttribute("stroke", "#a6e3a1");
      endDot.setAttribute("stroke-width", "2");
      svg.appendChild(endDot);
    }
    endDot.setAttribute("cx", String(cssX));
    endDot.setAttribute("cy", String(cssY));
  }

  /** pointerup — decide tap vs swipe, record step, inject on device. */
  async #handlePointerUp(e: PointerEvent): Promise<void> {
    const adb = this.#adb;
    const canvas = this.#canvas;
    const start = this.#gestureStart;
    if (!adb || !canvas || !start) return;

    this.#removeSvgTrail();
    this.#gestureStart = null;
    this.#gestureCurrent = null;
    this.#activePointerId = null;

    const rect = canvas.getBoundingClientRect();
    const endCssX = e.clientX - rect.left;
    const endCssY = e.clientY - rect.top;

    const dx = endCssX - start.cssX;
    const dy = endCssY - start.cssY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > SWIPE_THRESHOLD_PX) {
      // ── SWIPE ─────────────────────────────────────────────────────────
      const [fromX, fromY] = mapToDeviceCoords(
        start.cssX, start.cssY, rect.width, rect.height, canvas.width, canvas.height,
      );
      const [toX, toY] = mapToDeviceCoords(
        endCssX, endCssY, rect.width, rect.height, canvas.width, canvas.height,
      );
      const durationMs = Math.max(50, Math.min(1500, Date.now() - start.t));

      const step: import("@mimiq/ir").SwipeStep = {
        id: generateId(),
        type: "swipe",
        from: [fromX, fromY],
        to: [toX, toY],
        durationMs,
      };
      this.#appendOrReplace(step);

      // Inject swipe on device
      try {
        await adb.subprocess.noneProtocol.spawnWaitText([
          "input", "swipe",
          fromX.toString(), fromY.toString(),
          toX.toString(), toY.toString(),
          durationMs.toString(),
        ]);
      } catch (err) {
        console.error("[Recorder] injectSwipe failed:", err);
      }

    } else {
      // ── TAP (short/no movement) ────────────────────────────────────────
      const [deviceX, deviceY] = mapToDeviceCoords(
        start.cssX, start.cssY, rect.width, rect.height, canvas.width, canvas.height,
      );
      await this.#recordAndInjectTap(adb, canvas, deviceX, deviceY);
    }
  }

  /** Core tap recording (tree dump + hit-test + selector + inject). */
  async #recordAndInjectTap(
    adb: Adb,
    canvas: HTMLCanvasElement,
    deviceX: number,
    deviceY: number,
  ): Promise<void> {
    // ── Element capture mode: build selector and return it via callback ────
    // No step is appended, no tap is injected on the device.
    if (this.#captureCallback) {
      const cb = this.#captureCallback;
      this.#captureCallback = null;
      try {
        const capTree = await this.#dumpTree(adb);
        const capParentMap = buildParentMap(capTree);
        const capHit = hitTestTree(capTree, deviceX, deviceY);
        const capFlat = flattenTree(capTree);
        const capAncestors = capHit ? getAncestors(capHit, capParentMap) : [];
        const capSelector: Selector = capHit
          ? buildSelector(capHit, capFlat, capAncestors, { x: deviceX, y: deviceY }, { width: canvas.width, height: canvas.height })
          : { description: `coord(${deviceX},${deviceY})`, candidates: [] };
        cb(capSelector);
      } catch (err) {
        console.error("[Recorder] capture mode dumpTree failed:", err);
        cb({ description: `coord(${deviceX},${deviceY})`, candidates: [] });
      }
      return; // done — no step, no tap injection
    }

    // Dump tree BEFORE/AT tap time for accurate hit-testing
    let preTapTree: A11yNode[] = [];
    let parentMap = new Map<A11yNode, A11yNode>();
    let hit: A11yNode | null = null;
    try {
      preTapTree = await this.#dumpTree(adb);
      parentMap = buildParentMap(preTapTree);
      hit = hitTestTree(preTapTree, deviceX, deviceY);
    } catch (err) {
      console.error("[Recorder] dumpTree failed — recording coord-only step:", err);
    }

    // Check if on launcher for app-launch detection
    const prePkg = preTapTree[0]?.pkg ?? "";
    const onLauncher = prePkg !== "" && isLauncherPackage(prePkg);

    if (onLauncher) {
      try { await this.#injectTap(adb, deviceX, deviceY); } catch { /* ignore */ }
      const launchedPkg = await this.#waitForTreePkgChange(adb, prePkg);
      if (launchedPkg) {
        this.#appendOrReplace({ id: generateId(), type: "launchApp", packageName: launchedPkg } as LaunchAppStep);
        return;
      }
    }

    // Normal tap
    const flat = flattenTree(preTapTree);
    const ancestors = hit ? getAncestors(hit, parentMap) : [];
    const rawSelector = hit
      ? buildSelector(hit, flat, ancestors, { x: deviceX, y: deviceY }, { width: canvas.width, height: canvas.height })
      : null;
    const hasCandidates = (rawSelector?.candidates.length ?? 0) > 0;

    const selector: Selector = hasCandidates
      ? rawSelector!
      : { description: `coord(${deviceX},${deviceY})`, candidates: [] };

    this.#appendOrReplace({
      id: generateId(),
      type: "tap",
      selector,
      ...(!hasCandidates ? { fallbackCoord: [deviceX, deviceY] as [number, number] } : {}),
    } as TapStep);

    if (!onLauncher) {
      try { await this.#injectTap(adb, deviceX, deviceY); } catch (err) {
        console.error("[Recorder] injectTap failed:", err);
      }
    }
  }

  #removeSvgTrail(): void {
    if (this.#trailSvg) {
      this.#trailSvg.remove();
      this.#trailSvg = null;
    }
  }

  /** Append step or replace at replaceIndex, then notify. */
  #appendOrReplace(step: IRStep): void {
    if (this.#replaceIndex !== null) {
      const idx = this.#replaceIndex;
      this.#replaceIndex = null;
      this.#steps = [...this.#steps.slice(0, idx), step, ...this.#steps.slice(idx + 1)];
      this.#notifyListeners();
      this.#autoStop();
    } else {
      this.#steps = [...this.#steps, step];
      this.#notifyListeners();
    }
  }

  /**
   * Poll the accessibility tree until the root pkg changes from prePkg to a
   * non-launcher app. Returns the new package name, or null if no change.
   * Uses dumpTree() — guaranteed to work on any device that supports recording.
   */
  async #waitForTreePkgChange(adb: Adb, prePkg: string, maxMs = 2500): Promise<string | null> {
    const poll = 300;
    let elapsed = 0;
    while (elapsed < maxMs) {
      await sleepMs(poll);
      elapsed += poll;
      try {
        const tree = await this.#dumpTree(adb);
        const newPkg = tree[0]?.pkg ?? "";
        if (newPkg && newPkg !== prePkg && !isLauncherPackage(newPkg)) {
          return newPkg;
        }
      } catch { /* ignore transient dump failures */ }
    }
    return null;
  }

  /** Attach pointer listeners. opts.onAutoStop fires when a replace completes. */
  start(adb: Adb, canvas: HTMLCanvasElement, opts?: { onAutoStop?: () => void }): void {
    this.#detach();
    this.#adb = adb;
    this.#canvas = canvas;
    this.#autoStopCallback = opts?.onAutoStop ?? null;

    // pointerdown on the canvas — we only start a gesture when pressing on the mirror
    canvas.addEventListener("pointerdown", this.#onPointerDown);

    // pointermove/pointerup/pointercancel on document so they fire even when the
    // pointer leaves the canvas boundary during a drag (the common case for long swipes).
    // Fall back to canvas in test environments where document is not available.
    const docTarget = (typeof document !== "undefined" ? document : canvas) as EventTarget;
    docTarget.addEventListener("pointermove",   this.#onPointerMove);
    docTarget.addEventListener("pointerup",     this.#onPointerUp);
    docTarget.addEventListener("pointercancel", this.#onPointerCancel);
    this.#docTarget = docTarget;
  }

  /** Detach all pointer listeners (manual stop — clears replaceIndex). */
  stop(): void {
    this.#detach();
    this.#replaceIndex = null;
  }

  /** Internal detach — removes all listeners and clears refs.
   *  Preserves #replaceIndex so setReplaceMode(i) + start() works correctly. */
  #detach(): void {
    this.#canvas?.removeEventListener("pointerdown", this.#onPointerDown);
    this.#docTarget?.removeEventListener("pointermove",   this.#onPointerMove);
    this.#docTarget?.removeEventListener("pointerup",     this.#onPointerUp);
    this.#docTarget?.removeEventListener("pointercancel", this.#onPointerCancel);
    this.#docTarget = null;
    this.#removeSvgTrail();
    this.#activePointerId = null;
    this.#gestureStart = null;
    this.#gestureCurrent = null;
    this.#adb = null;
    this.#canvas = null;
    this.#autoStopCallback = null;
  }

  /** Internal auto-stop after a replace — fires onAutoStop. */
  #autoStop(): void {
    this.#canvas?.removeEventListener("pointerdown", this.#onPointerDown);
    this.#docTarget?.removeEventListener("pointermove",   this.#onPointerMove);
    this.#docTarget?.removeEventListener("pointerup",     this.#onPointerUp);
    this.#docTarget?.removeEventListener("pointercancel", this.#onPointerCancel);
    this.#docTarget = null;
    this.#removeSvgTrail();
    this.#activePointerId = null;
    this.#gestureStart = null;
    this.#gestureCurrent = null;
    this.#adb = null;
    this.#canvas = null;
    const cb = this.#autoStopCallback;
    this.#autoStopCallback = null;
    cb?.();
  }

  /**
   * Enter element-capture mode: the next tap on the mirror calls `callback`
   * with the built Selector. No step is appended and no ADB tap is injected.
   * Call `clearCaptureMode()` to cancel without capturing.
   */
  setCaptureMode(callback: (sel: Selector) => void): void {
    this.#captureCallback = callback;
  }

  /** Cancel element-capture mode without waiting for a tap. */
  clearCaptureMode(): void {
    this.#captureCallback = null;
  }

  /** Returns true if element-capture mode is currently active. */
  get captureModeActive(): boolean {
    return this.#captureCallback !== null;
  }

  /** Returns true if the recorder's canvas listeners are currently attached. */
  get isActive(): boolean {
    return this.#adb !== null;
  }

  /** Append an arbitrary step (command toolbar). */
  addStep(step: IRStep): void {
    this.#steps = [...this.#steps, step];
    this.#notifyListeners();
  }

  /** Remove the step at the given index. No-op if out of range. */
  deleteStep(index: number): void {
    if (index < 0 || index >= this.#steps.length) return;
    this.#steps = [...this.#steps.slice(0, index), ...this.#steps.slice(index + 1)];
    this.#notifyListeners();
  }

  /** Reorder steps to match the given array of step IDs. Used by dnd-kit after a drag ends. */
  reorderStepsByIds(orderedIds: string[]): void {
    const map = new Map(this.#steps.map((s) => [s.id, s]));
    const reordered = orderedIds.flatMap((id) => {
      const s = map.get(id);
      return s ? [s] : [];
    });
    // Append any steps not in orderedIds (should not happen, but safe)
    const reorderedSet = new Set(orderedIds);
    for (const s of this.#steps) {
      if (!reorderedSet.has(s.id)) reordered.push(s);
    }
    this.#steps = reordered;
    this.#notifyListeners();
  }

  /** Swap step at index with the adjacent step (up → index-1, down → index+1). No-op at bounds. */
  moveStep(index: number, direction: "up" | "down"): void {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= this.#steps.length) return;
    const steps = [...this.#steps];
    [steps[index], steps[targetIndex]] = [steps[targetIndex]!, steps[index]!];
    this.#steps = steps;
    this.#notifyListeners();
  }

  /** Replace step[index] in-place. Used by the edit form for non-tap steps. No-op if out of range. */
  updateStep(index: number, step: IRStep): void {
    if (index < 0 || index >= this.#steps.length) return;
    this.#steps = [
      ...this.#steps.slice(0, index),
      step,
      ...this.#steps.slice(index + 1),
    ];
    this.#notifyListeners();
  }

  /** Next canvas tap replaces steps[index] instead of appending. */
  setReplaceMode(index: number): void {
    this.#replaceIndex = index;
  }

  /** Move candidate[candidateIndex] in tap step[stepIndex] to position 0. */
  reorderCandidate(stepIndex: number, candidateIndex: number): void {
    const step = this.#steps[stepIndex];
    if (!step || step.type !== "tap" || candidateIndex === 0) return;
    const candidates = [...step.selector.candidates];
    const [chosen] = candidates.splice(candidateIndex, 1);
    if (!chosen) return;
    candidates.unshift(chosen);
    this.#steps = [
      ...this.#steps.slice(0, stepIndex),
      { ...step, selector: { ...step.selector, candidates } } as TapStep,
      ...this.#steps.slice(stepIndex + 1),
    ];
    this.#notifyListeners();
  }

  /** Remove candidate[candidateIndex] from tap step[stepIndex]. */
  removeCandidate(stepIndex: number, candidateIndex: number): void {
    const step = this.#steps[stepIndex];
    if (!step || step.type !== "tap") return;
    const candidates = step.selector.candidates.filter((_, i) => i !== candidateIndex);
    this.#steps = [
      ...this.#steps.slice(0, stepIndex),
      { ...step, selector: { ...step.selector, candidates } } as TapStep,
      ...this.#steps.slice(stepIndex + 1),
    ];
    this.#notifyListeners();
  }

  /** Update a candidate's strategy, value, and/or index in-place. No-op if indices are out of range. */
  updateCandidate(
    stepIndex: number,
    candidateIndex: number,
    updates: Partial<Pick<Candidate, "strategy" | "value" | "index">>,
  ): void {
    const step = this.#steps[stepIndex];
    if (!step || step.type !== "tap") return;
    if (candidateIndex < 0 || candidateIndex >= step.selector.candidates.length) return;
    const candidates = step.selector.candidates.map((c, i) =>
      i === candidateIndex ? { ...c, ...updates } : c,
    );
    this.#steps = [
      ...this.#steps.slice(0, stepIndex),
      { ...step, selector: { ...step.selector, candidates } } as TapStep,
      ...this.#steps.slice(stepIndex + 1),
    ];
    this.#notifyListeners();
  }

  /** Append a new candidate to a tap step. No-op if step is not a tap. */
  addCandidate(stepIndex: number, candidate: Candidate): void {
    const step = this.#steps[stepIndex];
    if (!step || step.type !== "tap") return;
    const candidates = [...step.selector.candidates, candidate];
    this.#steps = [
      ...this.#steps.slice(0, stepIndex),
      { ...step, selector: { ...step.selector, candidates } } as TapStep,
      ...this.#steps.slice(stepIndex + 1),
    ];
    this.#notifyListeners();
  }

  // ── Nested step mutations (for if/repeat/forEachRow branches) ─────────────

  /** Get the nested step array for a control-flow step's branch. */
  #getNestedSteps(parentStep: IRStep, branch: "then" | "else" | "steps"): IRStep[] | null {
    if (parentStep.type === "if") {
      if (branch === "then") return [...parentStep.then] as IRStep[];
      if (branch === "else") return [...(parentStep.else ?? [])] as IRStep[];
    }
    if (parentStep.type === "repeat" && branch === "steps") return [...parentStep.steps] as IRStep[];
    if (parentStep.type === "forEachRow" && branch === "steps") return [...parentStep.steps] as IRStep[];
    return null;
  }

  /** Build a mutated copy of a control-flow step with a new nested steps array. */
  #setNestedSteps(parentStep: IRStep, branch: "then" | "else" | "steps", nested: IRStep[]): IRStep {
    if (parentStep.type === "if") {
      if (branch === "then") return { ...parentStep, then: nested };
      if (branch === "else") return { ...parentStep, else: nested };
    }
    if (parentStep.type === "repeat" && branch === "steps") return { ...parentStep, steps: nested };
    if (parentStep.type === "forEachRow" && branch === "steps") return { ...parentStep, steps: nested };
    return parentStep;
  }

  /** Append a step into a control-flow branch. */
  addNestedStep(parentId: string, branch: "then" | "else" | "steps", step: IRStep): void {
    const idx = this.#steps.findIndex((s) => s.id === parentId);
    if (idx < 0) return;
    const parent = this.#steps[idx]!;
    const nested = this.#getNestedSteps(parent, branch);
    if (!nested) return;
    nested.push(step);
    this.#steps = [...this.#steps.slice(0, idx), this.#setNestedSteps(parent, branch, nested), ...this.#steps.slice(idx + 1)];
    this.#notifyListeners();
  }

  /** Remove a step from a control-flow branch by index. */
  deleteNestedStep(parentId: string, branch: "then" | "else" | "steps", index: number): void {
    const idx = this.#steps.findIndex((s) => s.id === parentId);
    if (idx < 0) return;
    const parent = this.#steps[idx]!;
    const nested = this.#getNestedSteps(parent, branch);
    if (!nested || index < 0 || index >= nested.length) return;
    nested.splice(index, 1);
    this.#steps = [...this.#steps.slice(0, idx), this.#setNestedSteps(parent, branch, nested), ...this.#steps.slice(idx + 1)];
    this.#notifyListeners();
  }

  /** Move a nested step up or down within its branch. */
  moveNestedStep(parentId: string, branch: "then" | "else" | "steps", index: number, direction: "up" | "down"): void {
    const idx = this.#steps.findIndex((s) => s.id === parentId);
    if (idx < 0) return;
    const parent = this.#steps[idx]!;
    const nested = this.#getNestedSteps(parent, branch);
    if (!nested) return;
    const targetIdx = direction === "up" ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= nested.length) return;
    [nested[index], nested[targetIdx]] = [nested[targetIdx]!, nested[index]!];
    this.#steps = [...this.#steps.slice(0, idx), this.#setNestedSteps(parent, branch, nested), ...this.#steps.slice(idx + 1)];
    this.#notifyListeners();
  }

  /** Replace a nested step in-place. */
  updateNestedStep(parentId: string, branch: "then" | "else" | "steps", index: number, step: IRStep): void {
    const idx = this.#steps.findIndex((s) => s.id === parentId);
    if (idx < 0) return;
    const parent = this.#steps[idx]!;
    const nested = this.#getNestedSteps(parent, branch);
    if (!nested || index < 0 || index >= nested.length) return;
    nested[index] = step;
    this.#steps = [...this.#steps.slice(0, idx), this.#setNestedSteps(parent, branch, nested), ...this.#steps.slice(idx + 1)];
    this.#notifyListeners();
  }

  /** Remove all steps. */
  clearSteps(): void {
    this.#steps = [];
    this.#notifyListeners();
  }

  get steps(): readonly IRStep[] {
    return this.#steps;
  }

  subscribe(listener: StepsListener): () => void {
    this.#listeners.add(listener);
    listener(this.#steps);
    return () => { this.#listeners.delete(listener); };
  }

  #notifyListeners(): void {
    const snapshot = this.#steps as readonly IRStep[];
    for (const l of this.#listeners) l(snapshot);
  }
}
